import React, { useMemo, useState, useEffect } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  useGetSubmissions,
  useGetPatients,
  useViewport,
  useCurrentUser,
} from "utils/hooks";
import {
  DataTable,
  Input,
  Icon,
  PageLoading,
  SidePanel,
  AssignmentIcon,
} from "components";
import useInboxDataColumns from "../utils/useInboxDataColumns";
import { AssignmentSubmission } from "types";
import { getPatientNameEmail } from "../utils/helpers";
import SubmissionsDataCards from "../../../Shared/Submissions/components/SubmissionsDataCards";
import SingleSubmission from "pages/AppPages/Shared/Submissions/components/SingleSubmission";
import { toAmericanDateFromUnix, toTimeFromUnix } from "utils/helpers";
import {
  useOrganizationSettings,
  getPatientWordPlural,
} from "utils/hooks/organizations/useOrganizationSettings";

interface InboxTableProps {
  tabKey?: string;
}

const InboxTable = ({ tabKey }: InboxTableProps) => {
  const { t } = useTranslation(["common"]);
  const { user } = useCurrentUser();
  const { settings: orgSettings } = useOrganizationSettings();
  const location = useLocation();
  const navigate = useNavigate();
  const { slug } = useParams();
  const [currentSubmission, setCurrentSubmission] = useState<
    AssignmentSubmission | undefined
  >(undefined);
  const { practiceSubmissions, practiceSubmission } = useGetSubmissions({
    slug: slug || currentSubmission?.slug,
  });
  const { patients } = useGetPatients({});
  const { isDesktop } = useViewport();
  const sortedSubmissions = useMemo(
    () =>
      practiceSubmissions
        ? [...practiceSubmissions]?.sort(
            (a: AssignmentSubmission, b: AssignmentSubmission) =>
              Number(b.createdAt) - Number(a.createdAt)
          )
        : undefined,
    [practiceSubmissions]
  );
  const [tabSubmissions, setTabSubmissions] = useState(sortedSubmissions);
  const [filteredSubmissions, setFilteredSubmissions] =
    useState(tabSubmissions);
  const [emptyState, setEmptyState] = useState("");
  const [showSubmission, setShowSubmission] = useState(false);
  const inboxDataColumns = useInboxDataColumns({
    submissionsTotal: filteredSubmissions?.length || 0,
    tabKey,
  });

  useEffect(() => {
    let result = sortedSubmissions;
    switch (tabKey) {
      case "all-inbox":
        result = sortedSubmissions;
        setEmptyState(t("inbox.all_inbox_empty"));
        break;
      case "unmatched":
        result = sortedSubmissions?.filter(
          (s: AssignmentSubmission) => s.userId === ""
        );
        setEmptyState(t("inbox.unmatched_empty"));
        break;
      case "unassigned":
        result = sortedSubmissions?.filter(
          (s: AssignmentSubmission) => s.userId && !s.connectedTherapistId
        );
        setEmptyState(
          t("inbox.unassigned_empty", {
            patientWordPlural: getPatientWordPlural(orgSettings),
          })
        );
        break;
    }
    setTabSubmissions(result);
  }, [tabKey, sortedSubmissions, t, orgSettings]);

  useEffect(() => setFilteredSubmissions(tabSubmissions), [tabSubmissions]);

  const rowProps = (row: any, rowRef: any) => {
    if (rowRef) {
      rowRef.onmouseover = () => {
        setCurrentSubmission(row.original);
      };
      rowRef.onclick = () => {
        if (row?.original?.slug) {
          navigate(`${location?.pathname}/${row.original.slug}`);
        }
        setCurrentSubmission(row.original);
        setShowSubmission(true);
      };
    }
  };

  const handleFilterSubmissions = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value.toLowerCase();
    if (inputValue === "") {
      setFilteredSubmissions(tabSubmissions);
      return;
    }
    const searchSubmissions = tabSubmissions?.filter(
      (s: AssignmentSubmission) => {
        const patientData = getPatientNameEmail(s.userId, patients);
        if (
          patientData?.name.toLowerCase().includes(inputValue) ||
          patientData?.email.includes(inputValue) ||
          s.userEmail?.includes(inputValue) ||
          s.userName?.toLowerCase().includes(inputValue)
        ) {
          return true;
        } else {
          return false;
        }
      }
    );
    setFilteredSubmissions(searchSubmissions);
  };

  useEffect(() => {
    if (slug) {
      setCurrentSubmission(practiceSubmission);
      setShowSubmission(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const TitleAssignmentIcon = () => {
    if (practiceSubmission) {
      return (
        <div className="flex">
          <AssignmentIcon
            style={practiceSubmission?.assignmentLabel}
            variant="small"
            color={practiceSubmission?.color}
            icon={practiceSubmission?.icon?.src}
          />
          {practiceSubmission?.assignmentLabel}
        </div>
      );
    }
    return null;
  };

  const handleCloseSubmissionSidePanel = () => {
    setCurrentSubmission(undefined);
    setShowSubmission(false);
    navigate(`/inbox/${tabKey}`);
  };

  return (
    <>
      {filteredSubmissions && (
        <>
          <div className="input-search inbox-table_input">
            <Input
              placeholder={t("common_labels.search")}
              inputExtraClass="input-search__input"
              onChange={handleFilterSubmissions}
            />
            <Icon src={"Search"} alt="magnifying glass" />
          </div>

          {isDesktop ? (
            <div className="inbox-table">
              <DataTable
                data={filteredSubmissions}
                columns={inboxDataColumns}
                id="inbox-submissions-table"
                dataType="submission"
                emptyStateComponent={
                  <div className="inbox-table_empty-state">{emptyState}</div>
                }
                rowProps={rowProps}
              />
            </div>
          ) : (
            <SubmissionsDataCards
              submissions={filteredSubmissions}
              onShowSubmission={() => setShowSubmission(true)}
              onSetSubmission={setCurrentSubmission}
            />
          )}
        </>
      )}
      {!filteredSubmissions && <PageLoading />}
      {
        <SidePanel
          title={
            <div className="flex">
              <TitleAssignmentIcon />
              <div>&nbsp;&nbsp;|&nbsp;&nbsp;</div>
              {toAmericanDateFromUnix(practiceSubmission?.createdAt)}{" "}
              {t("common_labels.at")}{" "}
              {toTimeFromUnix(practiceSubmission?.createdAt)}
            </div>
          }
          isVisible={practiceSubmission && showSubmission ? true : false}
          onClose={handleCloseSubmissionSidePanel}
          width={practiceSubmission?.isWideData ? "940" : "400"}
        >
          <SingleSubmission
            submission={practiceSubmission}
            groupId={practiceSubmission?.groupId || undefined}
            onClose={handleCloseSubmissionSidePanel}
            showMatch={user?.role === "Owner"}
          />
        </SidePanel>
      }
    </>
  );
};

export default InboxTable;
